import balanceText from 'balance-text'
import { dispatchEvent, VerticalScroll } from '@nrk/dh-basis-common'
import { setupClickNavigator } from './click-navigation.js'
import { setupKeyboardNavigator } from './keyboard-navigation.js'
import { balanceAndWatchPaddedText } from '../lib/balance-text-wrapper.js'
import { IsScrollingMixin } from './is-scrolling-mixin.ts'
import { setupScreenreaderNavigator } from './screenreader-navigation.js'
import { scrollSnapSupported, useIOSScrollfix } from './feature-support.ts'

import './vars.css'

const isActive = (element) =>
  element.getAttribute('data-state') === 'ACTIVE' &&
  element.parentElement.getAttribute('viewport-position') === 'IN-VIEWPORT'

export class KortstokkApp extends IsScrollingMixin(HTMLElement) {
  #screenReaderDetected = false
  #pointerDown = false
  #keyDown = false
  #safariScroller = null

  connectedCallback() {
    super.connectedCallback()

    this.addEventListener('dh-basis-scrollhistorie-trigger-activated', this._handleTriggerActivated)
    this.buttonUp.addEventListener('click', this._handleClickUp)
    this.buttonDown.addEventListener('click', this._handleClickDown)
    this.removeScreenreaderNavigator = setupScreenreaderNavigator(this)
    this.removeClickNavigator = setupClickNavigator(this)
    this.removeKeyboardNavigator = setupKeyboardNavigator(this)

    // TODO: Remove once we determine that there's no need to Support iOS 14
    if (!scrollSnapSupported) {
      // Disable scroll snap for older browsers
      const scrollingElement = document.scrollingElement
      scrollingElement.style.setProperty('scroll-snap-type', 'none')
    }

    // TODO: Remove balance-text once Safari supports `text-wrap: balance`
    if (!CSS.supports('text-wrap', 'balance')) {
      // Polyfill text-wrap: balance for .balance-text and .balance-text-padded
      requestAnimationFrame(() => {
        // Balances all text with '.balance-text'
        balanceText()
        // Fix for balancing text in padded spans (intro)
        balanceAndWatchPaddedText(this.querySelectorAll('.balance-text-padded'))
      })
    }

    /* Safari doesn't always scroll to anchor at load. Force scroll to element. */
    setTimeout(() => {
      if (window.location.hash) {
        const linkedElement = document.querySelector(`${window.location.hash}`)
        linkedElement?.scrollIntoViewIfNeeded?.()
      }
    }, 100)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.buttonUp.removeEventListener('click', this._handleClickUp)
    this.buttonDown.removeEventListener('click', this._handleClickDown)
    this.removeScreenreaderNavigator()
    this.removeClickNavigator()
    this.removeKeyboardNavigator()
  }

  _handleClickUp = (e) => {
    e.stopPropagation()
    this._navigateUp()
    dispatchEvent(this, 'dh-kortstokk-button-navigation')
  }

  _handleClickDown = (e) => {
    e.stopPropagation()
    this._navigateDown()
    dispatchEvent(this, 'dh-kortstokk-button-navigation')
  }

  _handleTriggerActivated = (e) => {
    const background = e.target.querySelector(`[data-background-id="${e.detail.backgroundId}"]`)
    this.hideLogo = background?.hasAttribute('data-hide-logo')
    const triggerElements = this.triggerElements
    const index = triggerElements.findIndex(isActive)

    if (index === 0) {
      this.buttonUp.setAttribute('disabled', '')
      this.buttonDown.removeAttribute('disabled')
    } else if (index === triggerElements.length - 1) {
      this.buttonUp.removeAttribute('disabled')
      this.buttonDown.setAttribute('disabled', '')
    } else {
      this.buttonUp.removeAttribute('disabled')
      this.buttonDown.removeAttribute('disabled')
    }

    /*
     * Dispatch synthetic mousemove event to signal activity to core-analytics.
     *
     * TODO: Remove once core-analytics have implemented support for activity signals, or improved detection of activity.
     * */
    let event
    if (typeof Event === 'function') {
      event = new Event('mousemove')
    } else {
      event = document.createEvent('Event')
      event.initEvent('mousemove', true, true)
    }
    document.dispatchEvent(event)
  }

  _navigateUp = () => {
    const triggerElements = this.triggerElements
    const index = triggerElements.findIndex(isActive)
    const nextElement = triggerElements[index - 1]
    if (!nextElement) {
      return
    }

    if (useIOSScrollfix || !('scrollBehavior' in this.style && this.scrollTo)) {
      requestAnimationFrame(() => this._safariSmoothScrollTo(nextElement.offsetTop))
    } else {
      nextElement.scrollIntoView({ behavior: 'smooth' })
    }
  }

  _navigateDown = () => {
    const triggerElements = this.triggerElements
    const index = triggerElements.findIndex(isActive)
    const nextElement = triggerElements[index + 1]
    if (!nextElement) {
      return
    }

    if (useIOSScrollfix || !('scrollBehavior' in this.style && this.scrollTo)) {
      requestAnimationFrame(() => this._safariSmoothScrollTo(nextElement.offsetTop))
    } else {
      nextElement.scrollIntoView({ behavior: 'smooth' })
    }
  }

  /**
   * Workaround for Safari 16 as it fails on smooth scroll into view
   * with scroll-snap in viewport.
   *
   * https://bugs.webkit.org/show_bug.cgi?id=245722
   * @param {number} scrollTop
   * @package
   */
  _safariSmoothScrollTo(scrollTop) {
    // eslint-disable-next-line compat/compat
    const scrollingElement = document.scrollingElement

    if (this.#safariScroller) {
      // Cancel ongoing scroll
      this.#safariScroller.cancel()
    }
    const safariScroller = new VerticalScroll(scrollingElement)
    this.#safariScroller = safariScroller
    // Not possible to animate scrollTop on root when scroll-snap-type is set.
    // Disable scroll snap
    scrollingElement.style.setProperty('scroll-snap-type', 'none')
    safariScroller.to(scrollTop, 600, () => {
      requestAnimationFrame(() => {
        if (safariScroller._cancelled) {
          console.warn('Scroller has already been cancelled')
          return
        }

        // Safari 14 resets scroll-top when scroll-snap-type on root is modified.
        // Scroll snap is no longer suppoorted for this browser
        // TODO: Remove this if-statement once we drop support of iOS 14
        if (scrollSnapSupported) {
          // Re-enable scroll snap
          scrollingElement.style.removeProperty('scroll-snap-type')
        }
        this.#safariScroller = null
      })
    })
  }

  get triggerElements() {
    return Array.from(this.querySelectorAll('[data-show-background]'))
  }

  get buttonUp() {
    return this.querySelector('button[value="up"]')
  }

  get buttonDown() {
    return this.querySelector('button[value="down"]')
  }

  get hideLogo() {
    return this.hasAttribute('hide-logo')
  }

  set hideLogo(val) {
    if (val) {
      this.setAttribute('hide-logo', '')
    } else {
      this.removeAttribute('hide-logo')
    }
  }
}
